import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import mainImage from '../assets/LSS_BG_GIF.gif';
import ledssBg from '../assets/LEDSS_BG.gif';

import device1 from '../assets/p5.gif';
import device2 from '../assets/red-tasman-devil.gif';
import device3 from '../assets/wombat.gif';
import device4 from '../assets/winding.png';
import device5 from '../assets/school-zone.gif';
import device6 from '../assets/your-logo.jpg';

import RealismButton from '../components/RealismButton';

import logo from '../assets/Animated- crop.gif';
import logo1 from '../assets/City_of_Moreton_Bay.png';
import logo2 from '../assets/vendorPanel.png';
import logo3 from '../assets/BIEPA.png';
import logo4 from '../assets/GYMPIE REGIONAL CONSUL.png';
import logo5 from '../assets/local-buy copy.png';
import logo6 from '../assets/somerset.png';
import logo7 from '../assets/Queensland-Government-logo.png';
import logo8 from '../assets/tmr.png';

gsap.registerPlugin(ScrollTrigger);

// Per-logo height, because the source files have different aspect ratios / internal
// padding — a single uniform size can't make them all look optically equal. Bump an
// individual `cls` (e.g. h-10 / h-12 / h-14 / h-16) to balance one that looks off.
const PARTNER_LOGOS = [
  { src: logo1 },
  { src: logo2 },
  { src: logo3 },
  { src: logo4 },
  { src: logo5 },
  { src: logo6 },
  { src: logo7 },
  { src: logo8 },
];

const FEATURES = [
  {
    title: 'Radar-Activated Speed Detection',
    desc: 'Advanced radar technology provides precise real-time vehicle speed detection with instant visual feedback to encourage safer driving behavior and reduce accident rates.',
  },
  {
    title: 'Off-Grid Solar-Powered Operation',
    desc: 'Energy-efficient solar systems ensure continuous 24/7 functionality in remote locations without external power infrastructure, supporting environmental sustainability initiatives.',
  },
  {
    title: 'Full-Color LED Display Options',
    desc: 'Customizable color schemes with adaptive brightness controls optimize visibility across all environmental conditions, delivering maximum driver attention through configurable warnings.',
  },
  {
    title: 'Programmable Display Messages',
    desc: 'Flexible configuration of speed thresholds, warning messages, and custom content tailored to diverse environments, road conditions, and specific safety requirements.',
  },
  {
    title: 'Web-Based Control Interface',
    desc: 'Intuitive remote management portal enables comprehensive configuration, monitoring, and system updates from any location, eliminating physical access requirements.',
  },
  {
    title: 'Traffic Data Logging',
    desc: 'Comprehensive data collection including vehicle speeds, volumes, and traffic patterns enables detailed analysis, reporting, and informed infrastructure planning decisions.',
  },
  {
    title: '4G Cloud Connectivity',
    desc: 'Secure 4G network connectivity ensures real-time system updates, remote access capabilities, and seamless communication between deployed units and central management.',
  },
  {
    title: 'Portable Mounting Options',
    desc: 'Flexible deployment solutions for temporary or rotating applications, ideal for roadworks, events, construction zones, and mobile enforcement operations.',
  },
  {
    title: 'Robust Anti-Tamper Design',
    desc: 'Advanced security features including GPS tracking, environmental sensors, and secure enclosures ensure tamper resistance and asset protection in high-risk environments.',
  },
  {
    title: 'Australian Manufactured & Hosted',
    desc: 'Proudly designed, manufactured, and supported in Australia, ensuring local compliance standards, rapid technical support, and complete data sovereignty for all clients.',
  },
];

const QUOTE =
  'Proudly partnering with government agencies, local councils, and industry leaders across Australia to enhance road safety.';

const PRODUCTS = [
  {
    title: 'RGB P5 LED Series',
    desc: '5mm full-color LED displays deliver exceptional clarity for fine text and high-resolution stationary and animated content. Ideal for detailed messaging applications requiring superior visual quality.',
    path: '/products',
    image: device1,
    shortDesc: '5mm full-color LED displays for high-resolution content',
    fullDesc: 'The RGB P5 Series features a 5mm pixel pitch that delivers stunning full-color clarity, perfect for detailed text and animated messaging. This premium display brings unmatched visual quality to any road safety or information application.',
    features: [
      '5mm pixel pitch for ultra-clear visuals',
      'Full-color RGB display capabilities',
      'Supports animated content and graphics',
      'Ideal for detailed messaging needs',
      'Upgraded solar and battery system for the P5\'s higher power consumption',
      'Real-time radar speed detection integrated',
      'Australian-based 4G cloud connectivity'
    ]
  },
  {
    title: 'RGB P10 LED Series',
    desc: 'Medium-resolution displays with 10mm pixel pitch provide optimal balance of clarity and efficiency. Perfect for urban roads, construction zones, and general traffic management applications.',
    path: '/products',
    image: device2,
    shortDesc: '10mm pixel pitch - balanced clarity & efficiency',
    fullDesc: 'The RGB P10 Series hits the sweet spot between visual clarity and power efficiency. This versatile workhorse is perfect for urban roads, construction sites, and all general traffic management needs across Australia.',
    features: [
      '10mm pixel pitch for balanced performance',
      'Full-color RGB display capabilities',
      'Optimized power consumption for extended runtime',
      'Suitable for all environments: roads, mine sites, & public spaces',
      'Standard solar and battery system for reliable operation',
      'Real-time radar speed detection integrated',
      'Australian-based 4G cloud connectivity'
    ]
  },
  {
    title: 'RG LED Series',
    desc: 'Dual-color red/green displays with 10mm pixel pitch offer strong visibility while optimizing power consumption for extended runtime in remote locations. Excellent for mining sites and temporary work zones.',
    path: '/products',
    image: device3,
    shortDesc: 'Red/green dual-color for remote & mining sites',
    fullDesc: 'Engineered for remote locations and temporary deployments, the RG LED Series uses dual-color red/green technology to maximize battery life while maintaining exceptional visibility. The top choice for mining sites and remote work zones.',
    features: [
      'Dual-color red/green LED technology',
      'Narrower viewing angles but exceptional long-distance visibility',
      'Lower power consumption for extended runtime',
      '10mm pixel pitch for reliable visibility',
      'Quick-deploy portable mounting options',
      'Australian-based 4G cloud connectivity'
    ]
  },
  {
    title: 'Warning Sign Series',
    desc: 'Specialized warning signs including winding road signs, turn signs, stop sign ahead, traffic light ahead, and other critical road safety indicators. Enhanced visibility for hazard awareness.',
    path: '/products',
    image: device4,
    shortDesc: 'Specialized hazard warning signs for roads',
    fullDesc: 'Our comprehensive Warning Sign Series covers every critical road hazard. From winding roads to intersection warnings, each sign features LED-enhanced visibility to ensure drivers see hazards day or night.',
    features: [
      'Covers all warning types: winding road, left/right turn, stop sign ahead, etc.',
      'Projector lens LED for optimal viewing distance & power saving',
      'Real-time radar speed detection integrated',
      'Adaptive brightness to prevent over-glare at night',
      'Solar-powered maintenance-free operation',
      'Weatherproof durable construction',
      'Optional Australian-based 4G cloud connectivity'
    ]
  },
  {
    title: 'Enhanced School Zone Series',
    desc: 'Custom-configured school zone speed signs with flashing beacons, time-based activation, and adaptive messaging to ensure maximum safety for children and pedestrians in school areas.',
    path: '/products',
    image: device5,
    shortDesc: 'Specialized school zone signs with time activation',
    fullDesc: 'Designed exclusively to protect children, the Enhanced School Zone Series includes time-based scheduling, flashing beacons, and adaptive messaging that activates automatically during school drop-off/pick-up hours.',
    features: [
      'Schedule school days up to 1 year in advance',
      'Optional 4G connectivity for web portal configuration',
      'Easy to manage signs across entire school regions',
      'Optional radar to detect & record traffic data',
      'Bright flashing warning beacons for high visibility',
      'Australian-based cloud hosting for secure data',
      'Fully meets all Australian school safety standards'
    ]
  },
  {
    title: 'Custom Solutions',
    desc: 'Tailored configurations including custom sizes, resolutions, mounting options, and specialized features to meet unique project requirements. Our engineering team works directly with clients to deliver bespoke solutions.',
    path: '/products',
    image: device6,
    shortDesc: 'Bespoke solutions for unique project needs',
    fullDesc: 'Every project has unique requirements. Our engineering team collaborates directly with you to design, build, and deploy custom LED signage that perfectly matches your specifications, no matter how unique.',
    features: [
      'Custom sizes, pixel pitches & display resolutions',
      'Specialized mounting options: permanent, portable, temporary',
      'Tailored feature development for unique requirements',
      'Custom warning sign configurations & messaging',
      'Flexible power options: solar, grid, hybrid',
      'Optional 4G, radar, or autonomous configurations',
      'Direct engineering collaboration from day one',
      'Full end-to-end project support & installation'
    ]
  },
];

const STATS = [
  {
    label: 'Real-time Monitoring Portal',
    value: '24/7 Access',
    desc: 'Monitor battery, temperature, brightness, content & speed limits',
  },
  {
    label: 'Advanced Scheduling',
    value: '1 Year Ahead',
    desc: 'Schedule content changes, speed limits & brightness adjustments',
  },
  {
    label: 'Traffic Analytics & Logs',
    value: 'Secure Storage',
    desc: 'Australian-based servers with comprehensive traffic logs',
  },
  {
    label: 'Extended Warranty Coverage',
    value: 'Unlimited*',
    desc: 'Continuous service agreements for long-term reliability',
  },
  {
    label: 'And Much More...',
    value: 'Comprehensive',
    desc: 'Installation, training, emergency response, compliance docs, reporting & custom integration',
  },
];

const SHIMMER_BUTTON =
  "relative overflow-hidden transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] before:content-[''] before:absolute before:inset-y-0 before:left-0 before:w-full before:-translate-x-full before:bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.25),transparent)] before:transition-transform before:duration-700 before:ease-out hover:before:translate-x-[200%]";

// A single editorial feature/product row laid out as title — number — description
// on one line, like a row in a table. `titleLeft` flips which side holds the title
// (features 01-05 -> left, 06+ -> right; products alternate). The element sitting
// on the left slides in from the left edge, the one on the right from the right
// edge (`.slide-l` / `.slide-r`, animated on desktop). DOM order is title, number,
// desc so mobile (single column) stacks in reading order; desktop positions each
// piece explicitly via col-start so it ignores DOM order.

const SplitPanel = ({ label, title, desc, titleLeft, index }) => (
  <div className="split-panel relative z-10 grid grid-cols-1 items-center gap-5 px-6 py-5 md:grid-cols-[auto_1fr] md:gap-8 md:py-6 md:px-[12vw] lg:px-[18vw]">

    {/* Title + Description - right side, perfect readability */}
    <div className="order-2 md:order-none text-center md:text-left">
      <h3 className="feature-title text-2xl font-bold leading-tight tracking-tight text-white mb-2">{title}</h3>
      <p className="max-w-[600px] text-base leading-relaxed text-white/75">{desc}</p>
    </div>
  </div>
);

const Home = () => {
  const navigate = useNavigate();

  // Products accordion — first item open by default (matches the reference layout).
  const [openProduct, setOpenProduct] = useState(0);

  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const imageWrapRef = useRef(null);
  const overlayRef = useRef(null);
  const heroContentRef = useRef(null);
  const featuresReelRef = useRef(null);
  const featuresMaskRef = useRef(null);
  const logoTrackRef = useRef(null);
  const quoteRef = useRef(null);
  const veilRef = useRef(null);
  const auroraRef = useRef([]);

  // Show the bottom blur veil only in the middle of the page — keep it off the
  // hero (first screen) and the footer (last screen).
  useEffect(() => {
    const updateVeil = () => {
      if (!veilRef.current) return;
      const vh = window.innerHeight;
      const y = window.scrollY;
      const docH = document.documentElement.scrollHeight;
      const overHero = y < vh * 0.9;
      const overFooter = y + vh > docH - 180;
      veilRef.current.style.opacity = overHero || overFooter ? '0' : '1';
    };
    updateVeil();
    window.addEventListener('scroll', updateVeil, { passive: true });
    window.addEventListener('resize', updateVeil);
    return () => {
      window.removeEventListener('scroll', updateVeil);
      window.removeEventListener('resize', updateVeil);
    };
  }, []);

  useEffect(() => {
    const docEl = document.documentElement;
    const { body } = document;
    const prev = {
      docHeight: docEl.style.height,
      docOverflow: docEl.style.overflow,
      bodyHeight: body.style.height,
      bodyOverflow: body.style.overflow,
    };
    docEl.style.height = 'auto';
    docEl.style.overflow = 'visible';
    body.style.height = 'auto';
    body.style.overflow = 'visible';

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      // ---- Full cinematic experience: desktop + motion allowed ----
      mm.add('(min-width: 768px) and (prefers-reduced-motion: no-preference)', () => {
        // Hero intro
        gsap
          .timeline({ defaults: { ease: 'power3.out', clearProps: 'transform' } })
          .fromTo('.hero-headline', { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.8 })
          .fromTo('.hero-subtext', { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.8 }, '-=0.5')
          .fromTo('.hero-cta', { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.8 }, '-=0.5')
          .fromTo('.hero-logos', { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.8 }, '-=0.4');

        // Partner marquee
        if (logoTrackRef.current) {
          gsap.to(logoTrackRef.current, {
            xPercent: -50,
            duration: 30,
            ease: 'none',
            repeat: -1,
          });
        }

        // Hero zoom-out + Features reel — all on ONE pinned image:
        //  • the centre gif grows to fill the screen and the dark overlay fades in;
        //  • then the 10 feature rows scroll upward as a single vertical reel,
        //    written directly over that dark overlay. Because it is one reel inside
        //    one pinned stage (no second image / section), every row — including
        //    08, 09, 10 — is guaranteed to scroll through and appear.
        const reel = featuresReelRef.current;
        const mask = featuresMaskRef.current;

        gsap.set(overlayRef.current, { autoAlpha: 0 });
        gsap.set(mask, { autoAlpha: 0 });

        const vh = window.innerHeight;
        const firstRow = reel.children[0];
        const lastRow = reel.children[reel.children.length - 1];
        const firstH = firstRow ? firstRow.offsetHeight : 0;
        const lastH = lastRow ? lastRow.offsetHeight : 0;
        const startY = vh / 2 - firstH / 2; // first row centred when the reel begins
        const endY = vh / 2 - reel.offsetHeight + lastH / 2; // last row centred at the end
        gsap.set(reel, { y: startY });

        // Relative timeline durations: 1 screen for the zoom, then a comfortable
        // reading pace for the reel (about half a screen of scroll per feature).
        const ZOOM = 1;
        const SCROLL = Math.max((startY - endY) / vh, reel.children.length * 0.1);

        gsap
          .timeline({
            defaults: { ease: 'none' },
            scrollTrigger: {
              trigger: stageRef.current,
              start: 'top top',
              end: () => '+=' + (ZOOM + SCROLL) * window.innerHeight,
              pin: true,
              scrub: true,
              anticipatePin: 1,
              invalidateOnRefresh: true,
            },
          })
          .fromTo(
            imageWrapRef.current,
            { width: '22vw', height: '55vh', borderRadius: '24px' },
            { width: '100vw', height: '100vh', borderRadius: '0px', duration: ZOOM },
            0
          )
          .to(heroContentRef.current, { autoAlpha: 0, duration: 0.6 }, 0)  // Fades hero out instantly
          .to(overlayRef.current, { autoAlpha: 1, duration: 0.6 }, 0.9)  // Fades in dark background
          .to(mask, { autoAlpha: 1, duration: 0.4 }, 0.7)                  // Fades in features
          .to(reel, { y: endY, duration: SCROLL }, ZOOM);

        // Editorial quote — scroll-scrubbed per-word reveal 
        gsap.fromTo(
          '.quote-word',
          { autoAlpha: 0.12, filter: 'blur(8px)' },
          {
            autoAlpha: 1,
            filter: 'blur(0px)',
            stagger: 0.4,
            ease: 'none',
            scrollTrigger: {
              trigger: quoteRef.current,
              start: 'top 80%',
              end: 'top 30%',
              scrub: 1,
            },
          }
        );

        // Service Plans stacking card: emerges from the bottom at ~0.92 scale and
        // grows to full size as it reaches the viewport center.
        gsap.utils.toArray('.stack-card').forEach((card) => {
          gsap.fromTo(
            card,
            { scale: 0.92, transformOrigin: '50% 0%' },
            {
              scale: 1,
              ease: 'none',
              scrollTrigger: {
                trigger: card,
                start: 'top bottom',
                end: 'top 35%',
                scrub: true,
              },
            }
          );
        });

        // Service Plan rows — agenciy-style bottom-up reveal: each row rises and
        // fades in as it enters the viewport, staggering down the list on scroll.
        gsap.utils.toArray('.service-row').forEach((row) => {
          gsap.fromTo(
            row,
            { autoAlpha: 0, y: 48 },
            {
              autoAlpha: 1,
              y: 0,
              duration: 0.8,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: row,
                start: 'top 88%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        });

        ScrollTrigger.refresh();
      });

      // ---- Lightweight motion: mobile, motion allowed (marquee only) ----
      mm.add('(max-width: 767px) and (prefers-reduced-motion: no-preference)', () => {
        if (logoTrackRef.current) {
          gsap.to(logoTrackRef.current, {
            xPercent: -50,
            duration: 22,
            ease: 'none',
            repeat: -1,
          });
        }
      });

      // ---- Pastel aurora drift (any size, motion allowed) ----
      // Extremely subtle, slow floating — translate only (no rotation, no scale),
      // long durations, small offsets — so the light field feels alive but calm.
      // Tweak `xPercent`/`yPercent` for distance and `duration` for slowness.
      mm.add('(prefers-reduced-motion: no-preference)', () => {
        const drift = [
          { xPercent: 6, yPercent: 5, duration: 26 },
          { xPercent: -5, yPercent: 7, duration: 32 },
          { xPercent: 7, yPercent: -4, duration: 29 },
          { xPercent: -6, yPercent: -6, duration: 35 },
        ];
        auroraRef.current.forEach((el, i) => {
          if (!el || !drift[i]) return;
          gsap.to(el, {
            ...drift[i],
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        });
      });
    }, rootRef);

    return () => {
      ctx.revert();
      docEl.style.height = prev.docHeight;
      docEl.style.overflow = prev.docOverflow;
      body.style.height = prev.bodyHeight;
      body.style.overflow = prev.bodyOverflow;
    };
  }, []);

  const pauseMarquee = () => {
    if (!logoTrackRef.current) return;
    gsap.getTweensOf(logoTrackRef.current).forEach((tween) => tween.pause());
  };

  const resumeMarquee = () => {
    if (!logoTrackRef.current) return;
    gsap.getTweensOf(logoTrackRef.current).forEach((tween) => tween.resume());
  };

  return (
    <div ref={rootRef} className="relative">
      {/* ============ HERO (zoom-out pinned stage) ============ */}
      <section ref={stageRef} className="relative h-screen w-full overflow-hidden bg-white">
        {/* Warm pastel aurora field — layered radial-gradient blobs over the white
            base. Each is heavily blurred with reduced opacity so they diffuse into
            one another, forming soft glowing light arcs (no defined shapes). They
            float very slowly (translate only) for a calm, premium atmosphere.
            Tweak: blob colour/opacity in each bg-[radial-gradient], `blur-[..]` for
            softness, and position (left/top/right/bottom) for the light layout. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
          <div className="absolute inset-[-15%]">
            {/* soft orange — top-left */}
            <div
              ref={(el) => (auroraRef.current[0] = el)}
              className="absolute left-[-6%] top-[-10%] h-[64vh] w-[64vh] rounded-full blur-[70px] bg-[radial-gradient(circle,rgba(255,176,102,0.55),transparent_70%)] will-change-transform"
            />
            {/* peach — upper center */}
            <div
              ref={(el) => (auroraRef.current[1] = el)}
              className="absolute left-[32%] top-[14%] h-[60vh] w-[60vh] rounded-full blur-[80px] bg-[radial-gradient(circle,rgba(255,201,168,0.50),transparent_70%)] will-change-transform"
            />
            {/* pink — right */}
            <div
              ref={(el) => (auroraRef.current[2] = el)}
              className="absolute right-[-6%] top-[4%] h-[56vh] w-[56vh] rounded-full blur-[75px] bg-[radial-gradient(circle,rgba(255,183,197,0.50),transparent_70%)] will-change-transform"
            />
            {/* light yellow — bottom-right */}
            <div
              ref={(el) => (auroraRef.current[3] = el)}
              className="absolute right-[8%] bottom-[-12%] h-[62vh] w-[62vh] rounded-full blur-[80px] bg-[radial-gradient(circle,rgba(255,235,168,0.55),transparent_72%)] will-change-transform"
            />
          </div>
        </div>

        {/* Center gif card — the hero centerpiece that enlarges to fill the screen on scroll */}
        <div
          ref={imageWrapRef}
          className="absolute inset-0 z-0 m-auto h-[60vh] w-[80vw] overflow-hidden rounded-[12px] border border-white/15 shadow-[0_30px_80px_rgba(0,0,0,0.55)] md:h-[55vh] md:w-[22vw]"
          style={{
            backgroundImage: `url(${mainImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'right center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          {/* Left-side scrim so the overlaid headline stays legible */}
          <div className="absolute bg-[linear-gradient(120deg,rgba(13,18,38,0.6)_0%,rgba(13,18,38,0.18)_50%,transparent_78%)]" />
        </div>

        {/* Expand overlay (fades in as the image fills — becomes the darkened hero frame) */}
        <div
          ref={overlayRef}
            className="absolute inset-0 bg-[linear-gradient(180deg,rgba(10,15,35,0.95),rgba(5,8,20,0.95))] opacity-0"        
          />
        {/* Hero content — left headline + right column over the gif; trusted row below all three */}
        <div ref={heroContentRef} className="absolute inset-0 z-10">
          {/* LEFT + CENTER (gif behind) + RIGHT */}
          <div className="absolute inset-0 flex flex-col items-start justify-center gap-8 px-6 md:flex-row md:items-center md:justify-between md:px-6 lg:px-12 xl:px-[8vw] mx-0">
            {/* LEFT: headline + button, overlaid on top of the center gif */}
            <div className="relative z-20 max-w-[420px]">
              <img src={logo} alt="Company Logo" className="h-[18vh] w-auto object-contain mb-5" />

              <h1 className="hero-headline text-3xl font-extrabold leading-[1.1] tracking-[-0.5px] text-[#0f172a] md:text-4xl lg:text-3xl">
                  Speed Signs That Work Everywhere. Easy to Manage. Built to Last.
              </h1>
              <div className="hero-cta mt-6">
                <button
                  onClick={() => navigate('/products')}
                  className={`${SHIMMER_BUTTON} rounded-[14px] bg-[linear-gradient(135deg,#4ade80,#0a84ff)] px-8 py-3 text-[1.05rem] font-extrabold text-white hover:-translate-y-1 hover:scale-105`}
                >
                  Explore Our Products
                </button>
              </div>
            </div>

            {/* RIGHT: quote */}
            <div className="relative z-20 max-w-[300px] md:max-w-[320px]">
              <div className="hero-subtext">
                <p className="text-sm md:text-base italic leading-[1.7] text-slate-600 lg:text-lg">
                  Solar-powered radar speed detection signs that automatically record vehicle speeds and display adaptive safety messages. With reliable 4G connectivity, customizable content scheduling, and an easy-to-use portal for live monitoring and remote updates.
                </p>
              </div>
              
            </div>
          </div>

          {/* TRUSTED — placed below the left / center / right components */}
          <div className="hero-logos absolute inset-x-0 bottom-[1vh] px-8 md:px-[6vw]">
            <p className=" text-center text-xs font-semibold uppercase tracking-[0.25em] text-slate-400 pb-6">
              Trusted across Australia
            </p>
            <div className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
              <div
                ref={logoTrackRef}
                className="flex w-max items-center"
                onMouseEnter={pauseMarquee}
                onMouseLeave={resumeMarquee}
              >
                {[...PARTNER_LOGOS, ...PARTNER_LOGOS].map((item, index) => (
                  <img
                    key={index}
                    src={item.src}
                    alt={`Partner ${(index % PARTNER_LOGOS.length) + 1}`}
                    className={`mx-8 mt-0 mb-8 h-16 w-auto flex-shrink-0 object-contain [filter:brightness(1.2)]`}
                  />
                ))}
              </div>
            </div>
            <div className="absolute bottom-[-10px] left-1/2 transform -translate-x-1/2 z-30 animate-bounce">
              <svg className="w-10 h-10 text-green-400 drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">                
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {/* FEATURES 01-10 reel (desktop + motion). Sits over the dark overlay on the
            zoomed gif; all 10 rows scroll up through here as one pinned reel, so the
            features are written on the very same zoom-out image — no second image. */}
        <div
          ref={featuresMaskRef}
          className="absolute inset-0 z-20 opacity-100 md:block"
        >
          <div
            ref={featuresReelRef}
            className="absolute left-0 right-0 top-[5vh] flex flex-col overflow-x-clip will-change-transform"
          >
            <h1 className="mb-4 bg-[linear-gradient(135deg,#ff3b30,#30d158,#0a84ff)] bg-clip-text text-center text-5xl font-bold text-transparent">Key Features</h1>
            {FEATURES.map((feature, i) => (
              <div key={i} className="py-2">
                <SplitPanel
                  index={i}
                  label={String(i + 1).padStart(2, '0')}
                  title={feature.title}
                  desc={feature.desc}
                  titleLeft={true}
                />
              </div>
            ))}
            
          </div>
        </div>
      </section> 

      {/* ============ FEATURES — mobile / reduced-motion stacked fallback ============ */}
      <section className="bg-[#0d1226] px-6 py-20 md:hidden motion-reduce:md:block">
        <div className="mx-auto flex max-w-[640px] flex-col gap-10">
          {FEATURES.map((feature, i) => (
            <div key={i} className="border-t border-white/10 pt-8">
              <div className="mb-3 bg-[linear-gradient(135deg,#ff3b30,#30d158,#0a84ff)] bg-clip-text text-3xl font-extrabold leading-none text-transparent">
                {String(i + 1).padStart(2, '0')}
              </div>
              <h3 className="text-2xl font-extrabold tracking-tight text-white">{feature.title}</h3>
              <p className="mt-3 leading-relaxed text-white/70">{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ Editorial quote band (scroll-scrubbed reveal) ============ */}
      <section ref={quoteRef} className="relative bg-white px-6 py-32 md:py-48">
        <p className="mx-auto max-w-[1100px] text-center text-3xl font-semibold leading-[2.2] tracking-tight md:text-5xl">
          {QUOTE.split(' ').map((word, i) => (
            <span key={i} className="quote-word mr-[0.3em] inline-block text-[#0f172a]">
              {word}
            </span>
          ))}
        </p>
      </section>

      {/* ===== Products & Services + Service Plans — sticky stacking cards ===== */}
      <div className="relative z-10 bg-white">
        <section className=" relative z-10 flex min-h-screen flex-col justify-center overflow-hidden  bg-white px-6 py-24  before:absolute before:left-0 before:right-0 before:top-0 before:h-px before:bg-[linear-gradient(90deg,transparent,#ff3b30,#30d158,#0a84ff,transparent)] before:content-[''] md:px-12">
          <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 items-start gap-8 md:grid-cols-[280px_1fr_320px] md:gap-10">
            {/* LEFT COLUMN: Product selector (your request) */}
            <div className="md:sticky md:top-28 md:self-start">
              <span className="mb-5 inline-block bg-[linear-gradient(135deg,#ff3b30,#30d158,#0a84ff)] bg-clip-text text-xs font-semibold uppercase tracking-[0.25em] text-transparent">
                Select Product
              </span>
              <div className="flex flex-col gap-4">
                {PRODUCTS.map((product, index) => {
                  const open = openProduct === index;
                  return (
                    <button
                      key={index}
                      onClick={() => setOpenProduct(index)}
                      className={`text-left p-5 rounded-2xl transition-all duration-500 transform hover:scale-[1.02] ${
                        open 
                          ? 'bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-500 shadow-xl shadow-green-500/10 -translate-x-1' 
                          : 'bg-white border border-slate-200 hover:bg-slate-50 hover:shadow-lg hover:border-slate-300'
                      }`}
                    >
                      <h3 className={`font-semibold text-sm ${open ? 'text-slate-900' : 'text-slate-700'}`}>
                        {product.title}
                      </h3>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{product.shortDesc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* MIDDLE COLUMN: Product image (your request - centered middle) */}
            <div className="md:sticky md:top-28 md:self-start flex flex-col items-start">
              {/* Raw image, no spacing issues */}
              <div className="relative w-full mt-0">
                {PRODUCTS.map((product, index) => (
                  <img
                    key={index}
                    src={product.image}
                    alt={product.title}
                    className={`w-full h-auto max-h-[70vh] object-contain transition-all duration-500 ease-out ${
                      index === openProduct ? 'opacity-100 static' : 'opacity-0 absolute'
                    }`}
                  />
                ))}
              </div>
            </div>
                {/* RIGHT COLUMN: Product description (your request) */}
            <div className="md:sticky md:top-28 md:self-start">
              <div className="bg-white rounded-2xl p-7 border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-500">
                <span className="mb-2 inline-block bg-[linear-gradient(135deg,#ff3b30,#30d158,#0a84ff)] bg-clip-text text-[10px] font-semibold uppercase tracking-[0.25em] text-transparent">
                  Product Details
                </span>
                <h3 className="text-xl font-bold text-slate-900 mb-4">{PRODUCTS[openProduct].title}</h3>
                <p className="text-sm leading-relaxed text-slate-600 mb-6">{PRODUCTS[openProduct].fullDesc}</p>
                <div className="border-t border-slate-100 pt-5">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">Highlights</h4>
                  <ul className="space-y-3">
                    {PRODUCTS[openProduct].features?.map((desc, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-slate-700 transition-all duration-300 hover:translate-x-1">
                        <span className="text-green-500 mt-0.5 text-base">✓</span>
                        {desc}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="pointer-events-none absolute -right-[150px] -top-[150px] z-0 h-[400px] w-[400px] rounded-full bg-[radial-gradient(circle,rgba(255,59,48,0.06)_0%,transparent_70%)]" />
          <div className="pointer-events-none absolute -bottom-[150px] -left-[150px] z-0 h-[400px] w-[400px] rounded-full bg-[radial-gradient(circle,rgba(48,209,88,0.06)_0%,transparent_70%)]" />
        </section>

        {/* ============ Service Plans — "Our Award" style row list ============ */}
        <section className="relative z-20 -mt-10 flex min-h-screen flex-col justify-center overflow-hidden bg-white px-6 py-24 md:px-12">
          <div className="mx-auto w-full max-w-[1180px]">
            {/* Header — title left, eyebrow right, divider under */}

<h2 className="text-[2.25rem] font-extrabold tracking-[-1px] text-[#0f172a] md:text-[3rem]">
                Comprehensive Service Plans
              </h2>
            {/* Award-style list — support level · title (bold) · short description */}
            <div className="mt-14 flex flex-col">
              {STATS.map((item, idx) => (
                <div
                  key={idx}
                  className="service-row group grid grid-cols-1 gap-2 border-t border-slate-200 py-7 transition-colors duration-300 last:border-b hover:bg-slate-50 md:grid-cols-[0.7fr_1.3fr_1.6fr] md:items-center md:gap-10"
                >
                  {/* Support level */}
                  <div className="bg-[linear-gradient(135deg,#ff3b30,#30d158,#0a84ff)] bg-clip-text text-xs font-semibold uppercase tracking-[0.2em] text-transparent">
                    {item.value}
                  </div>
                  {/* Title */}
                  <h3 className="text-[1.3rem] font-bold tracking-tight text-[#0f172a] transition-transform duration-300 md:text-[1.5rem] md:group-hover:translate-x-2">
                    {item.label}
                  </h3>
                  {/* Short description */}
                  <p className="text-[0.95rem] leading-[1.6] text-slate-500">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Footnote + CTA */}
            <div className="mt-12 flex flex-col items-center gap-4 text-center">
              <button
                onClick={() => navigate('/service-plans')}
                className={`${SHIMMER_BUTTON} rounded-[14px] bg-[linear-gradient(135deg,#4ade80,#0a84ff)] px-9 py-[12px] text-[1.1rem] font-extrabold text-white hover:-translate-y-1 hover:scale-105`}
              >
                Explore All Plans
              </button>

              <p className="text-[0.8rem] italic text-slate-400">
                *Features vary depending on selected service agreement package
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* ============ Viewport bottom blur veil (screen-space layer) ============ */}
      {/* Hidden over the hero (top) and the footer (bottom) — see the veil effect. */}
      <div
        ref={veilRef}
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-40 h-[18vh] opacity-0 transition-opacity duration-300 md:h-[12vh]"
        style={{
          background:
            'linear-gradient(to top, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.12) 55%, transparent 100%)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          maskImage: 'linear-gradient(to top, black 0%, black 38%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to top, black 0%, black 38%, transparent 100%)',
        }}
      />
    </div>
  );
};

export default Home;
